import React from 'react';

interface BoxProps {
  component?: keyof JSX.IntrinsicElements;
  sx?: React.CSSProperties & {
    display?: string | { xs?: string; sm?: string; md?: string };
    width?: string | number;
    height?: string | number;
    p?: number;
    px?: number;
    py?: number;
    m?: number;
    mx?: number;
    my?: number;
    mt?: number;
    mb?: number;
    ml?: number;
    mr?: number;
    bgcolor?: string;
    color?: string;
    border?: string;
    borderRadius?: number | string;
    boxShadow?: number | string;
    alignItems?: string;
    justifyContent?: string;
    [key: string]: any;
  };
  p?: number;
  px?: number;
  py?: number;
  m?: number;
  mx?: number;
  my?: number;
  mt?: number;
  mb?: number;
  ml?: number;
  mr?: number;
  display?: string;
  width?: string | number;
  height?: string | number;
  alignItems?: string;
  justifyContent?: string;
  className?: string;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
}

const Box: React.FC<BoxProps> = ({
  component = 'div',
  sx,
  className = '',
  children,
  onClick,
  p,
  px,
  py,
  m,
  mx,
  my,
  mt,
  mb,
  ml,
  mr,
  display,
  width,
  height,
  alignItems,
  justifyContent,
}) => {
  const Component = component;

  const getDisplayClass = (display?: string | { xs?: string; sm?: string; md?: string }) => {
    if (!display) return '';
    if (typeof display === 'string') {
      if (display === 'flex') return 'flex';
      if (display === 'grid') return 'grid';
      if (display === 'block') return 'block';
      return '';
    }
    return Object.entries(display)
      .map(([breakpoint, val]) => {
        const breakpointMap: Record<string, string> = {
          xs: '',
          sm: 'sm:',
          md: 'md:',
        };
        return `${breakpointMap[breakpoint] || ''}${val === 'flex' ? 'flex' : val === 'grid' ? 'grid' : 'block'}`;
      })
      .join(' ');
  };

  const displayValue = display || sx?.display;
  const widthValue = width !== undefined ? width : sx?.width;
  const heightValue = height !== undefined ? height : sx?.height;
  const alignItemsValue = alignItems || sx?.alignItems;
  const justifyContentValue = justifyContent || sx?.justifyContent;

  const style: React.CSSProperties = {
    ...(widthValue && { width: typeof widthValue === 'number' ? `${widthValue}px` : widthValue }),
    ...(heightValue && { height: typeof heightValue === 'number' ? `${heightValue}px` : heightValue }),
    ...(p !== undefined && { padding: `${p * 0.25}rem` }),
    ...(px !== undefined && { paddingLeft: `${px * 0.25}rem`, paddingRight: `${px * 0.25}rem` }),
    ...(py !== undefined && { paddingTop: `${py * 0.25}rem`, paddingBottom: `${py * 0.25}rem` }),
    ...(m !== undefined && { margin: `${m * 0.25}rem` }),
    ...(mx !== undefined && { marginLeft: `${mx * 0.25}rem`, marginRight: `${mx * 0.25}rem` }),
    ...(my !== undefined && { marginTop: `${my * 0.25}rem`, marginBottom: `${my * 0.25}rem` }),
    ...(mt !== undefined && { marginTop: `${mt * 0.25}rem` }),
    ...(mb !== undefined && { marginBottom: `${mb * 0.25}rem` }),
    ...(ml !== undefined && { marginLeft: `${ml * 0.25}rem` }),
    ...(mr !== undefined && { marginRight: `${mr * 0.25}rem` }),
    ...(sx?.p && { padding: `${sx.p * 0.25}rem` }),
    ...(sx?.px && { paddingLeft: `${sx.px * 0.25}rem`, paddingRight: `${sx.px * 0.25}rem` }),
    ...(sx?.py && { paddingTop: `${sx.py * 0.25}rem`, paddingBottom: `${sx.py * 0.25}rem` }),
    ...(sx?.m && { margin: `${sx.m * 0.25}rem` }),
    ...(sx?.mx && { marginLeft: `${sx.mx * 0.25}rem`, marginRight: `${sx.mx * 0.25}rem` }),
    ...(sx?.my && { marginTop: `${sx.my * 0.25}rem`, marginBottom: `${sx.my * 0.25}rem` }),
    ...(sx?.mt && { marginTop: `${sx.mt * 0.25}rem` }),
    ...(sx?.mb && { marginBottom: `${sx.mb * 0.25}rem` }),
    ...(sx?.ml && { marginLeft: `${sx.ml * 0.25}rem` }),
    ...(sx?.mr && { marginRight: `${sx.mr * 0.25}rem` }),
    ...(sx?.bgcolor && { backgroundColor: sx.bgcolor }),
    ...(sx?.color && { color: sx.color }),
    ...(sx?.border && { border: sx.border }),
    ...(sx?.borderRadius && { borderRadius: typeof sx.borderRadius === 'number' ? `${sx.borderRadius}px` : sx.borderRadius }),
    ...(sx?.boxShadow && { boxShadow: typeof sx.boxShadow === 'number' ? `0 ${sx.boxShadow}px ${sx.boxShadow * 2}px rgba(0,0,0,0.1)` : sx.boxShadow }),
    ...(alignItemsValue && { alignItems: alignItemsValue }),
    ...(justifyContentValue && { justifyContent: justifyContentValue }),
    ...(sx && typeof sx === 'object' && !Array.isArray(sx) ? (() => {
      const rest: Record<string, any> = {};
      Object.keys(sx).forEach(key => {
        if (!['display', 'width', 'height', 'p', 'px', 'py', 'm', 'mx', 'my', 'mt', 'mb', 'ml', 'mr', 'bgcolor', 'color', 'border', 'borderRadius', 'boxShadow', 'alignItems', 'justifyContent'].includes(key)) {
          rest[key] = sx[key];
        }
      });
      return rest;
    })() : {}),
  };

  const displayClass = getDisplayClass(displayValue);
  const classes = `${displayClass} ${className}`;

  return (
    <Component className={classes} style={style} onClick={onClick}>
      {children}
    </Component>
  );
};

export default Box;

